import React, { useEffect } from 'react';
import { CircleHelp, Download, FileUp, Map, Printer, X, XCircle } from 'lucide-react';
import './VendorPortalHelpModal.css';

export interface VendorPortalHelpModalProps {
  onClose: () => void;
  onTakeTour?: () => void;
  showTimber?: boolean;
  showCnc?: boolean;
}

const VendorPortalHelpModal: React.FC<VendorPortalHelpModalProps> = ({
  onClose,
  onTakeTour,
  showTimber = true,
  showCnc = false,
}) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div className="vph-overlay" role="presentation" onClick={onClose}>
      <div
        className="vph-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vph-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="vph-header">
          <div className="vph-header-main">
            <div className="vph-icon" aria-hidden>
              <CircleHelp size={18} />
            </div>
            <div>
              <h2 id="vph-title" className="vph-title">
                How bidding works
              </h2>
              <p className="vph-subtitle">
                A guide to reviewing packages, submitting quotes, and using this portal.
              </p>
            </div>
          </div>
          <button type="button" className="vph-close" onClick={onClose} aria-label="Close help">
            <X size={18} />
          </button>
        </header>

        <div className="vph-body">
          <section className="vph-section">
            <h3>The bidding process</h3>
            <ol className="vph-steps">
              <li>
                <strong>Invitation</strong> — LC Timber Frames publishes an RFQ package and sends your
                organization an email with a link to this portal.
              </li>
              <li>
                <strong>Review</strong> — Select a project in the sidebar, pick a package version, and
                download the timber list or 3D model for the release you are quoting.
              </li>
              <li>
                <strong>Respond</strong> — Upload your quote with a total dollar amount, or mark{' '}
                <em>Not bidding</em> if you cannot quote that version.
              </li>
              <li>
                <strong>Updates</strong> — If we publish a revised package, a new version appears in the
                version tree. Each version is quoted separately; your earlier responses stay on record.
              </li>
            </ol>
            {onTakeTour && (
              <p className="vph-note">
                New to the portal? Use <strong>Take a tour</strong> in the header for an interactive
                walkthrough of the layout and buttons.
              </p>
            )}
          </section>

          <section className="vph-section">
            <h3>Projects sidebar</h3>
            <p>
              The left sidebar lists every LC Timber Frames project assigned to your organization. Each row
              shows the project number, descriptor, work types (Timber and/or CNC), your latest materials bid
              total, and whether a quote is still needed.
            </p>
          </section>

          <section className="vph-section">
            <h3>Version tree</h3>
            <p>
              The vertical timeline on the left of each work section lists every published version (v1, v2,
              …). Click a version to view its actions and submit a quote for that release only.
            </p>
            <ul className="vph-list">
              <li>
                <strong>Latest</strong> — The most recent release from LC Timber Frames.
              </li>
              <li>
                A green dot means you submitted a quote for that version; gray means you marked not bidding.
              </li>
              <li>
                <strong>Your quotes by version</strong> at the bottom summarizes all responses when multiple
                versions exist.
              </li>
            </ul>
          </section>

          {showTimber && (
            <section className="vph-section">
              <h3>Timber RFQ — actions</h3>
              <ul className="vph-actions-list">
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <Download size={14} />
                  </span>
                  <div>
                    <strong>CSV</strong> — Download the timber list spreadsheet to build your quote offline.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <Printer size={14} />
                  </span>
                  <div>
                    <strong>PDF</strong> — Open a printable timber list with the same quantities and
                    species.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <Download size={14} />
                  </span>
                  <div>
                    <strong>3D Model</strong> — Download a Cadwork, IFC, STEP, DXF, or SAT file when one is released with this version.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <FileUp size={14} />
                  </span>
                  <div>
                    <strong>Upload quote</strong> — Submit your quote PDF with a total dollar amount.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <XCircle size={14} />
                  </span>
                  <div>
                    <strong>Not bidding</strong> — Decline this version so our team knows you will not quote.
                  </div>
                </li>
              </ul>
              <p>
                Species and dryness badges show what each quote row covers. Include delivery location and lead
                time in your uploaded quote.
              </p>
            </section>
          )}

          {showCnc && (
            <section className="vph-section">
              <h3>CNC quote — actions</h3>
              <ul className="vph-actions-list">
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <Download size={14} />
                  </span>
                  <div>
                    <strong>3D Model</strong> — Download the Cadwork, IFC, STEP, DXF, or SAT file published for this package version.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <FileUp size={14} />
                  </span>
                  <div>
                    <strong>Upload quote</strong> — Submit your CNC quote PDF with a total dollar amount.
                  </div>
                </li>
                <li>
                  <span className="vph-action-icon" aria-hidden>
                    <XCircle size={14} />
                  </span>
                  <div>
                    <strong>Not bidding</strong> — Decline this CNC package version.
                  </div>
                </li>
              </ul>
              <p>Review the 3D model before quoting. Prior uploads appear under your supplier action group.</p>
            </section>
          )}

          <section className="vph-section">
            <h3>Changing or removing a response</h3>
            <p>
              After you submit, your response appears under the package. You can open uploaded files or remove
              your response if you need to replace it — delete first, then upload again. To switch from a quote
              to not bidding (or the reverse), remove your existing response first.
            </p>
          </section>
        </div>

        <footer className="vph-footer">
          {onTakeTour && (
            <button type="button" className="vp-btn" onClick={onTakeTour}>
              <Map size={14} aria-hidden /> Take a tour
            </button>
          )}
          <button type="button" className="vp-btn primary" onClick={onClose}>
            Got it
          </button>
        </footer>
      </div>
    </div>
  );
};

export default VendorPortalHelpModal;
